import { format, sub } from 'date-fns';
import { useIntl } from 'react-intl';

import { IOperatingHours, IRestaurantNode } from 'generated/rbi-graphql';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { ServiceMode } from 'state/service-mode';
import {
  isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours,
  readableCloseHourToday,
} from 'utils/restaurant';

import { storeHoursSummary } from '../constants';

const getOperatingHours = (restaurant: IRestaurantNode) => (serviceMode: ServiceMode) => {
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      return restaurant.driveThruHours;
    case ServiceMode.CURBSIDE:
      return restaurant.curbsideHours;
    default:
      return restaurant.diningRoomHours;
  }
};

const isWithinOneHourOfClose = (hours: IOperatingHours) => {
  return !isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(hours, new Date(Date.now()), 60);
};

const getHoursToday = (operatingHours: IOperatingHours) => {
  const today = new Date(Date.now());
  const todayWeekday = format(today, 'iiii').toLowerCase();
  const yesterdayWeekday = format(sub(today, { days: 1 }), 'iiii').toLowerCase();

  const [hoursToday] = storeHoursSummary
    .filter(([weekName]) => weekName.toLowerCase() === todayWeekday)
    .map(([, weekAbbreviated]) => {
      const openingHours = operatingHours[`${weekAbbreviated}Open`] || undefined;
      const closingHours = operatingHours[`${weekAbbreviated}Close`] || undefined;

      return { openingHours, closingHours };
    });

  const [hoursYesterday] = storeHoursSummary
    .filter(([weekName]) => {
      return weekName.toLowerCase() === yesterdayWeekday;
    })
    .map(([, weekAbbreviated]) => {
      const yesterdayClosingHours = operatingHours[`${weekAbbreviated}Close`] || undefined;
      const yesterdayOpeningHours = operatingHours[`${weekAbbreviated}Open`] || undefined;

      return { yesterdayClosingHours, yesterdayOpeningHours };
    });

  return { ...hoursToday, ...hoursYesterday };
};

interface IUseOpenClosedTextValue {
  isOpen: boolean;
  text: string;
  operatingHours?: {
    openingHours: string | undefined;
    closingHours: string | undefined;
    yesterdayClosingHours: string | undefined;
    yesterdayOpeningHours: string | undefined;
  };
}

export const useOpenClosedText = (
  restaurant: IRestaurantNode,
  validateCapableServiceMode = false
): IUseOpenClosedTextValue => {
  const { availablePickupServiceModes, capablePickupServiceModes } =
    useServiceModeStatus(restaurant);

  const availablePickup = validateCapableServiceMode
    ? capablePickupServiceModes
    : availablePickupServiceModes;

  const { formatMessage } = useIntl();

  // Get the open hours for the pickup service mode that is open and not disabled.
  const allOperatingHours = availablePickup.map(getOperatingHours(restaurant));

  // Filter the operating hours to get the first set of hours that are not all null.
  const [operatingHours] = allOperatingHours.filter(hours => {
    if (!hours) {
      return false;
    }

    return !Object?.values(hours as unknown as string).every(value => {
      if (value === 'OperatingHours') {
        return true;
      }
      return value === null;
    });
  });

  if (!operatingHours) {
    return { isOpen: false, text: formatMessage({ id: 'closed' }) };
  }

  const hours = getHoursToday(operatingHours);

  if (isWithinOneHourOfClose(operatingHours)) {
    return {
      isOpen: true,
      text: formatMessage({ id: 'closesAt' }, { time: readableCloseHourToday(operatingHours) }),
      operatingHours: hours,
    };
  }

  return { isOpen: true, text: formatMessage({ id: 'open' }), operatingHours: hours };
};
